/**
 * The two places the console installs a key, and the questions that follow.
 *
 * A scope says **where the console puts a key**, never what the key may do
 * once it is there (`docs/AUTH.md` §4). There are two of them, and not three,
 * because the console writes exactly two `authorized_keys` files: the one in
 * your own workspaces, and the one on the single machine it keeps off the
 * cluster — the machine that reaches MLflow from a run on somebody else's
 * cluster, and the address Jean Zay accepts us from. Those last two are the
 * same machine with the same `sshd`, so they cannot be separate boxes to tick.
 */

import type { KeyScope, RegisteredKey } from "../api/types";

export interface KeyScopeChoice {
  value: KeyScope;
  /** What the section is called on screen. */
  label: string;
  /** Where the key is installed, in one sentence. */
  meaning: string;
}

/**
 * What the interface owes anybody who reads *Training elsewhere*.
 *
 * The machine pulls its key list itself, about once a minute, and the console
 * does not watch it happen (`docs/AUTH.md` §4b) — the same sentence the
 * *Training elsewhere* screen carries. Exported so that the form which
 * registers a key and the list of keys already on file say it in the same
 * words — a caveat that drifts between two screens is a caveat nobody trusts.
 */
export const ELSEWHERE_SYNC_NOTE =
  "That machine picks the key list up about once a minute, so a key saved " +
  "just now lands there about a minute later — if a first try is refused, " +
  "wait a minute and try again.";

/**
 * The scopes, in the order the API serves them back.
 *
 * The slugs are named for what a researcher reads — the *Training elsewhere*
 * section — rather than for the machine we operate.
 */
export const KEY_SCOPES: readonly KeyScopeChoice[] = [
  {
    value: "workspaces",
    label: "Your workspaces",
    meaning:
      "The console puts this key in every workspace you open from now on.",
  },
  {
    value: "elsewhere",
    label: "Training elsewhere",
    meaning:
      "The console puts this key on the machine off the cluster: the one that " +
      "reaches MLflow from a run somewhere else, and the one Jean Zay accepts " +
      `us from. ${ELSEWHERE_SYNC_NOTE}`,
  },
];

/**
 * What a new key carries unless the researcher says otherwise.
 *
 * Both, because that is exactly what every key already on an account does:
 * ticking both is yesterday's product, and somebody who does not care about
 * any of this never has to think about it.
 */
export const DEFAULT_KEY_SCOPES: readonly KeyScope[] = [
  "workspaces",
  "elsewhere",
];

/**
 * The keys the console would install in a given place.
 *
 * Args:
 *     keys: Every key on file.
 *     scope: The place.
 *
 * Returns:
 *     Those of them carrying that scope, in the order given.
 */
export function keysForScope(
  keys: readonly RegisteredKey[],
  scope: KeyScope,
): RegisteredKey[] {
  return keys.filter((key) => key.scopes.includes(scope));
}

/**
 * Whether anything on file would be installed in a given place.
 *
 * Args:
 *     keys: Every key on file.
 *     scope: The place.
 *
 * Returns:
 *     True when at least one key carries that scope.
 */
export function hasKeyFor(
  keys: readonly RegisteredKey[],
  scope: KeyScope,
): boolean {
  return keys.some((key) => key.scopes.includes(scope));
}

/**
 * Put a scope on a key, or take it off, keeping the canonical order.
 *
 * Args:
 *     key: The key to change.
 *     scope: The scope being ticked or unticked.
 *     installed: Whether the key should carry it.
 *
 * Returns:
 *     A new key; the original is untouched.
 */
export function withScope(
  key: RegisteredKey,
  scope: KeyScope,
  installed: boolean,
): RegisteredKey {
  const wanted = new Set(key.scopes);
  if (installed) {
    wanted.add(scope);
  } else {
    wanted.delete(scope);
  }
  return {
    ...key,
    scopes: KEY_SCOPES.map((choice) => choice.value).filter((value) =>
      wanted.has(value),
    ),
  };
}

/**
 * How a key is named on screen.
 *
 * The label wins when there is one, because it is what the researcher chose;
 * otherwise the key's own comment, which `ssh-keygen` fills with the machine
 * that made it. With neither, the row says so rather than inventing a name.
 *
 * Args:
 *     label: The label on file, when there is one.
 *     comment: The key's trailing comment.
 *
 * Returns:
 *     The name to show.
 */
export function keyName(label: string | null, comment: string): string {
  return label || comment || "This key names no machine";
}
